import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type AccesoPortal, type Fiduciante } from "@/lib/api";
import {
  aPayloadFiduciante,
  formFiduciante,
  sinDistribucionVigente,
  validarEmailAcceso,
  validarFiduciante,
  type ErroresFiduciante,
  type FormFiduciante,
  type PayloadFiduciante,
} from "@/lib/participacion";
import { temporalGenerada, type TemporalGenerada } from "@/lib/usuarios";

type Dialogo =
  | { tipo: "ficha"; fiduciante: Fiduciante }
  | { tipo: "form"; fiduciante: Fiduciante | null }
  | { tipo: "borrar"; fiduciante: Fiduciante }
  | { tipo: "email"; fiduciante: Fiduciante }
  | { tipo: "acceso"; nombre: string; temporal: TemporalGenerada | null }
  | null;

const SIN_ERRORES: ErroresFiduciante = {};

/** Lógica de Fiduciantes (sin JSX). El contador ve la lista; sólo el admin tiene acciones. */
export function useFiduciantesTab() {
  const { fid, membresia, usuario, recargarUsuario } = useContextoApp();
  const navigate = useNavigate();
  const base = `/api/fideicomisos/${fid}/fiduciantes`;
  const lista = useApiResource<Fiduciante[]>(base);
  const esAdmin = membresia.rol === "admin";
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormFiduciante>(() => formFiduciante());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [email, setEmail] = useState("");
  const [mostrarErrorEmail, setMostrarErrorEmail] = useState(false);

  const accionGuardar = useAsyncAction((id: number | null, datos: PayloadFiduciante) =>
    id === null ? api.post<Fiduciante>(base, datos) : api.patch<Fiduciante>(`${base}/${id}`, datos),
  );
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`${base}/${id}`).then(() => true));
  const accionAcceso = useAsyncAction((id: number) => api.post<AccesoPortal>(`${base}/${id}/acceso`));
  // Sin email no hay acceso: se lo pide en el momento, lo guarda en la ficha y sigue con el acceso (Mati, 2026-10-19).
  const accionEmailYAcceso = useAsyncAction((id: number, datos: { email: string }) =>
    api.patch<Fiduciante>(`${base}/${id}`, datos).then(() => api.post<AccesoPortal>(`${base}/${id}/acceso`)),
  );
  const accionVerComo = useAsyncAction((id: number) =>
    api.put<void>(`/api/fideicomisos/${fid}/ver-como`, { fiduciante_id: id }).then(() => true),
  );

  const errores = validarFiduciante(form);
  const cerrar = () => setDialogo(null);

  const abrirForm = (fiduciante: Fiduciante | null) => {
    setForm(formFiduciante(fiduciante ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", fiduciante });
  };

  const cambiar = (campo: keyof FormFiduciante, valor: string) => setForm((f) => ({ ...f, [campo]: valor }));

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.fiduciante?.id ?? null, aPayloadFiduciante(form))) {
      cerrar();
      lista.recargar();
    }
  };

  const abrirBorrar = (fiduciante: Fiduciante) => {
    accionBorrar.limpiarError();
    setDialogo({ tipo: "borrar", fiduciante });
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.fiduciante.id)) {
      cerrar();
      lista.recargar();
    }
  };

  const mostrarAcceso = (fiduciante: Fiduciante, r: AccesoPortal) => {
    lista.recargar();
    setDialogo({
      tipo: "acceso",
      nombre: fiduciante.nombre,
      temporal: r.password_temporal ? temporalGenerada(fiduciante.nombre, r.password_temporal) : null,
    });
  };

  const darAcceso = async (fiduciante: Fiduciante) => {
    if (!fiduciante.email) {
      setEmail("");
      setMostrarErrorEmail(false);
      accionEmailYAcceso.limpiarError();
      setDialogo({ tipo: "email", fiduciante });
      return;
    }
    const r = await accionAcceso.run(fiduciante.id);
    if (r) mostrarAcceso(fiduciante, r);
  };

  const errorEmail = validarEmailAcceso(email);
  const confirmarEmail = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrorEmail(true);
    if (dialogo?.tipo !== "email" || errorEmail) return;
    const r = await accionEmailYAcceso.run(dialogo.fiduciante.id, { email: email.trim() });
    if (r) mostrarAcceso(dialogo.fiduciante, r);
  };

  /** "Ver como": la sesión pasa a responder como el usuario de la ficha (sólo lectura) y la app arranca de nuevo en el Inicio. */
  /** Con usuario activo y que no sea uno mismo (el backend da 409 en los otros casos). */
  const puedeVerComo = (f: Fiduciante) => f.usuario_id !== null && f.usuario_id !== usuario.id && f.estado_portal !== "desactivado";
  // Un solo botón (Mati, 2026-11-02: "un botón por cada fiduciante no va"): entra como el primero por nombre y en la
  // franja de arriba se elige a quién ver.
  const primeroParaVer = (lista.data ?? []).filter(puedeVerComo).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"))[0];
  const verComo = async () => {
    if (!primeroParaVer || !(await accionVerComo.run(primeroParaVer.id))) return;
    await recargarUsuario();
    navigate("/");
  };

  return {
    fiduciantes: lista.data ?? [],
    cargando: lista.loading && !lista.data,
    errorCarga: lista.data ? null : (lista.error?.message ?? null),
    reintentar: lista.recargar,
    esAdmin,
    /** Aviso para el admin: hay fiduciantes y hoy no rige ninguna distribución (llamados y gastos no se reparten). */
    avisoSinDistribucion:
      esAdmin && lista.data && sinDistribucionVigente(lista.data)
        ? "Hoy no rige ninguna distribución completa: las previsiones de gastos y los gastos no se le reparten a nadie."
        : null,
    dialogo,
    cerrar,
    form,
    cambiar,
    errores: mostrarErrores ? errores : SIN_ERRORES,
    abrirFicha: (f: Fiduciante) => setDialogo({ tipo: "ficha", fiduciante: f }),
    abrirNuevo: () => abrirForm(null),
    abrirEditar: (f: Fiduciante) => abrirForm(f),
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    abrirBorrar,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    darAcceso,
    errorAcceso: accionAcceso.error,
    email,
    cambiarEmail: setEmail,
    errorEmail: mostrarErrorEmail ? errorEmail : null,
    confirmarEmail,
    guardandoEmail: accionEmailYAcceso.loading,
    errorGuardarEmail: accionEmailYAcceso.error,
    puedeVerComo: esAdmin && primeroParaVer !== undefined,
    verComo,
    entrandoVerComo: accionVerComo.loading,
    errorVerComo: accionVerComo.error,
  };
}
